// src/components/AgenticFlowDemo.jsx
// v4.2 — Agentic AI: Explicador Interactivo
// 4 columnas: Proceso/Señal → Agente → Contexto → Sistemas/Acción
// Navegación manual + autoplay opcional
// Sin backend. Sin API. Sin inputs del usuario.

import React, { useState, useEffect, useRef } from 'react';
import {
  Zap, Brain, Database, BookOpen, Wrench, Bell, ShieldAlert,
  Check, AlertTriangle, ArrowRight, Play, Pause, ChevronLeft, ChevronRight,
} from 'lucide-react';

// ─── CONFIGURACIÓN DE NODOS (Dark Premium System) ───────────────────
const NODE_CFG = {
  signal: { 
    label: 'Señal', Icon: Zap, color: 'sky',
    activeBg: 'bg-sky-950/25', activeBorder: 'border-sky-400', activeText: 'text-sky-100', activeIcon: 'text-sky-400',
    activeGlow: 'shadow-lg shadow-sky-500/20', completedIcon: 'text-sky-700'
  },
  agent: { 
    label: 'Agente', Icon: Brain, color: 'violet',
    activeBg: 'bg-violet-950/25', activeBorder: 'border-violet-400', activeText: 'text-violet-100', activeIcon: 'text-violet-400',
    activeGlow: 'shadow-lg shadow-violet-500/20', completedIcon: 'text-violet-700'
  },
  memory: { 
    label: 'Memoria', Icon: Database, color: 'amber',
    activeBg: 'bg-amber-950/20', activeBorder: 'border-amber-400', activeText: 'text-amber-100', activeIcon: 'text-amber-400',
    activeGlow: 'shadow-lg shadow-amber-500/20', completedIcon: 'text-amber-700'
  },
  rag: { 
    label: 'RAG', Icon: BookOpen, color: 'cyan',
    activeBg: 'bg-cyan-950/25', activeBorder: 'border-cyan-400', activeText: 'text-cyan-100', activeIcon: 'text-cyan-400',
    activeGlow: 'shadow-lg shadow-cyan-500/20', completedIcon: 'text-cyan-700'
  },
  tools: { 
    label: 'Tools', Icon: Wrench, color: 'emerald',
    activeBg: 'bg-emerald-950/20', activeBorder: 'border-emerald-400', activeText: 'text-emerald-100', activeIcon: 'text-emerald-400',
    activeGlow: 'shadow-lg shadow-emerald-500/20', completedIcon: 'text-emerald-700'
  },
  action: { 
    label: 'Acción', Icon: Zap, color: 'orange',
    activeBg: 'bg-orange-950/20', activeBorder: 'border-orange-400', activeText: 'text-orange-100', activeIcon: 'text-orange-400',
    activeGlow: 'shadow-lg shadow-orange-500/20', completedIcon: 'text-orange-700'
  },
  result: { 
    label: 'Resultado', Icon: Bell, color: 'teal',
    activeBg: 'bg-teal-950/25', activeBorder: 'border-teal-400', activeText: 'text-teal-100', activeIcon: 'text-teal-400',
    activeGlow: 'shadow-lg shadow-teal-500/20', completedIcon: 'text-teal-700'
  },
  human_review: {
    label: 'Revisión humana', Icon: ShieldAlert, color: 'rose',
    activeBg: 'bg-rose-950/15', activeBorder: 'border-rose-400', activeText: 'text-rose-100', activeIcon: 'text-rose-400',
    activeGlow: 'shadow-lg shadow-rose-500/20', completedIcon: 'text-rose-700'
  },
};

// ─── DATOS DE DEMO: 2 MODOS × 7 PASOS ───────────────────────────────
const DEMO_MODES = {
  structured: {
    label: 'Proceso estructurado',
    bannerFinal: 'El agente puede intervenir porque el proceso tiene reglas claras, datos confiables y puntos de acción configurados.',
    bannerVariant: 'success',
    steps: [
      {
        id: 1, fromLane: 'process', toLane: 'agent', fromNode: 'signal', toNode: 'agent',
        title: 'Ingreso de señal operativa',
        mainText: 'El sistema registra una caída de stock crítico en la tienda 27.',
        transitionText: 'Antes de intervenir, el agente debe validar si esta señal tiene antecedentes relevantes en el proceso.',
        traceText: '[Señal] Evento registrado en tienda 27',
        bullets: ['fuente: inventario sincronizado', 'evento: stock crítico', 'prioridad inicial: media'],
        status: 'normal',
      },
      {
        id: 2, fromLane: 'agent', toLane: 'context', fromNode: 'agent', toNode: 'memory',
        title: 'Validación de antecedentes',
        mainText: 'El agente consulta si este evento ya ocurrió recientemente en el mismo punto operativo.',
        transitionText: 'Si encuentra reincidencia, debe comprobar si existe una regla operativa aplicable.',
        traceText: '[Memoria] Consultando historial reciente del caso',
        bullets: ['objetivo: validar reincidencia', 'foco: tienda 27', 'decisión aún no ejecutada'],
        status: 'normal',
      },
      {
        id: 3, fromLane: 'context', toLane: 'agent', fromNode: 'memory', toNode: 'agent',
        title: 'Antecedentes confirmados',
        mainText: 'La memoria confirma que hubo un incidente similar hace 3 días.',
        transitionText: 'Con la reincidencia confirmada, debe verificar si existe una regla operativa aplicable.',
        traceText: '[Memoria] Historial encontrado: incidente hace 3 días',
        bullets: ['tienda recurrente: 27', 'antecedente: alerta previa registrada', 'patrón: reincidencia reciente'],
        status: 'normal',
      },
      {
        id: 4, fromLane: 'agent', toLane: 'context', fromNode: 'agent', toNode: 'rag',
        title: 'Validación de reglas del proceso',
        mainText: 'El agente consulta las reglas del proceso para verificar si la reincidencia activa una intervención formal.',
        transitionText: 'Si la regla aplica, debe confirmar el estado real en el sistema operativo.',
        traceText: '[RAG] Consultando reglas operativas aplicables',
        bullets: ['umbral crítico esperado', 'criterio de reincidencia', 'política de escalamiento'],
        status: 'normal',
      },
      {
        id: 5, fromLane: 'context', toLane: 'systems', fromNode: 'rag', toNode: 'tools',
        title: 'Regla confirmada',
        mainText: 'La regla confirma que si el stock cae por debajo del umbral y hay reincidencia, debe escalarse la incidencia.',
        transitionText: 'Con la regla confirmada, debe validar el estado real del inventario en el sistema operativo.',
        traceText: '[RAG] Regla encontrada: umbral < 15 y reincidencia < 7 días',
        bullets: ['regla: sí definida', 'siguiente validación: inventario actual', 'intervención potencial: habilitada'],
        status: 'normal',
      },
      {
        id: 6, fromLane: 'systems', toLane: 'systems', fromNode: 'tools', toNode: 'action',
        title: 'Intervención ejecutada',
        mainText: 'El agente consulta el sistema, confirma 9 unidades disponibles y ejecuta la acción definida por el proceso: alerta crítica con reposición prioritaria.',
        transitionText: 'El resultado debe volver al proceso para dejar trazabilidad y notificar al equipo responsable.',
        traceText: '[Tool] Inventario validado: 9 unidades',
        bullets: ['prioridad: alta', 'destino: abastecimiento', 'acción: reposición prioritaria'],
        status: 'success',
      },
      {
        id: 7, fromLane: 'systems', toLane: 'process', fromNode: 'result', toNode: 'signal',
        title: 'Resultado registrado en el flujo',
        mainText: 'La intervención queda registrada y el proceso continúa bajo control con el equipo notificado.',
        transitionText: 'El flujo queda actualizado y el caso permanece trazable para seguimiento posterior.',
        traceText: '[Resultado] Flujo actualizado y equipo notificado',
        bullets: ['alerta creada correctamente', 'equipo notificado', 'estado del caso: monitoreado'],
        status: 'success',
      },
    ],
  },

  unstructured: {
    label: 'Proceso no estructurado',
    bannerFinal: 'El agente detecta la anomalía, pero no puede intervenir con confianza porque el proceso no tiene reglas claras, datos confiables o puntos de acción definidos.',
    bannerVariant: 'warning',
    steps: [
      {
        id: 1, fromLane: 'process', toLane: 'agent', fromNode: 'signal', toNode: 'agent',
        title: 'Ingreso de señal ambigua',
        mainText: 'El sistema registra una posible caída de stock en la tienda 27, pero la señal entra con inconsistencias.',
        transitionText: 'Antes de intervenir, el agente debe validar si existe suficiente contexto histórico confiable.',
        traceText: '[Señal] Evento registrado con inconsistencias de origen',
        bullets: ['fuente duplicada', 'timestamp inconsistente', 'confianza inicial: baja'],
        status: 'normal',
      },
      {
        id: 2, fromLane: 'agent', toLane: 'context', fromNode: 'agent', toNode: 'memory',
        title: 'Validación de antecedentes',
        mainText: 'El agente intenta recuperar historial confiable del mismo caso.',
        transitionText: 'Si no encuentra continuidad histórica, debe revisar si al menos existe una regla operativa clara.',
        traceText: '[Memoria] Validando si existe contexto suficiente',
        bullets: ['objetivo: validar reincidencia', 'objetivo: confirmar trazabilidad', 'intervención aún no habilitada'],
        status: 'normal',
      },
      {
        id: 3, fromLane: 'context', toLane: 'agent', fromNode: 'memory', toNode: 'agent',
        title: 'Antecedentes inconsistentes',
        mainText: 'La memoria no ofrece historial confiable del caso.',
        transitionText: 'Sin continuidad histórica, debe revisar si al menos existe una regla operativa clara que compense la falta de datos.',
        traceText: '[Memoria] No se encontró historial confiable',
        bullets: ['incidentes duplicados', 'responsables no definidos', 'contexto incompleto'],
        status: 'normal',
      },
      {
        id: 4, fromLane: 'agent', toLane: 'context', fromNode: 'agent', toNode: 'rag',
        title: 'Validación de reglas del proceso',
        mainText: 'El agente consulta si el proceso define un umbral formal y un protocolo aplicable para decidir la intervención.',
        transitionText: 'Si no encuentra reglas suficientes, intentará una validación mínima en los sistemas antes de decidir si puede intervenir.',
        traceText: '[RAG] Consultando criterios operativos del proceso',
        bullets: ['búsqueda de umbral crítico', 'búsqueda de protocolo', 'validación de escalamiento'],
        status: 'normal',
      },
      {
        id: 5, fromLane: 'context', toLane: 'systems', fromNode: 'rag', toNode: 'tools',
        title: 'Validación mínima en sistemas',
        mainText: 'Como el proceso no ofrece reglas suficientemente claras, el agente intenta verificar si los sistemas entregan evidencia operativa mínima para decidir.',
        transitionText: 'Si los sistemas no entregan datos confiables, no podrá habilitar una intervención automática.',
        traceText: '[Tool] Consultando inventario y consistencia de datos',
        bullets: ['no hay umbral unificado', 'no hay protocolo consistente', 'validación de inventario en curso'],
        status: 'normal',
      },
      {
        id: 6, fromLane: 'systems', toLane: 'agent', fromNode: 'tools', toNode: 'agent',
        title: 'Intervención no habilitada',
        mainText: 'Los sistemas no entregan evidencia suficiente y el proceso no ofrece reglas claras para actuar con confianza.',
        transitionText: 'Dado que no puede ejecutar una acción automática con precisión, debe derivar el caso a revisión humana.',
        traceText: '[Tool] Inventario no sincronizado',
        bullets: ['datos desactualizados', 'sincronización incompleta', 'validación insuficiente para automatizar'],
        status: 'blocked',
      },
      {
        id: 7, fromLane: 'agent', toLane: 'manual_exit', fromNode: 'agent', toNode: 'human_review',
        title: 'Escalamiento a revisión humana',
        mainText: 'El agente registra la anomalía y deriva el caso a revisión manual, sin ejecutar intervención automática.',
        transitionText: 'El siguiente paso queda a cargo de un operador humano, que evaluará el caso y decidirá la acción correspondiente.',
        traceText: '[Caso] Escalado a revisión humana',
        bullets: ['intervención no ejecutada', 'caso derivado manualmente', 'recomendación: ordenar reglas, datos y flujo'],
        status: 'blocked',
      },
    ],
  },
};

// ─── SUBCOMPONENTE: NODO DEL SWIMLANE (Dark Premium) ────────────────
function SwimLaneNode({ nodeId, isActive, isCompleted, isSource, isTarget }) {
  const cfg = NODE_CFG[nodeId];
  const NodeIcon = cfg.Icon;

  // Sistema dark-premium: fondos oscuros teñidos + bordes brillantes + texto claro
  const boxCls =
    isActive   ? `${cfg.activeBg} ${cfg.activeBorder} ${cfg.activeGlow} scale-105 border-2` :
    isCompleted ? `border-slate-800 bg-slate-900/40 opacity-75` :
    'border-slate-800/50 bg-slate-900/30 opacity-45';

  const iconCls =
    isActive    ? cfg.activeIcon :
    isCompleted ? cfg.completedIcon :
    'text-slate-700';

  const textCls =
    isActive    ? cfg.activeText :
    isCompleted ? 'text-slate-400' :
    'text-slate-600';

  return (
    <div className={`
      relative flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-xl border transition-all duration-300 text-center min-w-[80px]
      ${boxCls}
    `}>
      <span className={`transition-colors duration-300 ${iconCls}`}>
        <NodeIcon className="w-4 h-4" />
      </span>
      <span className={`text-[10px] font-medium leading-tight transition-colors duration-300 ${textCls}`}>
        {cfg.label}
      </span>
    </div>
  );
}

// ─── DIAGRAMA SWIMLANE DESKTOP ───────────────────────────────────────
function DesktopSwimlane({ stepIndex, steps }) {
  const step = steps[stepIndex];
  const completedSteps = steps.slice(0, stepIndex);

  const isNodeActive = (nodeId) => nodeId === step.fromNode || nodeId === step.toNode;
  const isNodeCompleted = (nodeId) => completedSteps.some(s => s.fromNode === nodeId || s.toNode === nodeId);

  return (
    <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5 backdrop-blur-sm">
      <p className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold mb-4">
        Intervención del agente sobre el proceso
      </p>

      {/* Grid de 4 columnas */}
      <div className="grid grid-cols-4 gap-3">
        {/* Columna 1: Evento del proceso */}
        <div className="flex flex-col gap-2">
          <p className="text-[9px] text-slate-400 uppercase tracking-wider font-semibold mb-1 text-center">
            Evento del proceso
          </p>
          <SwimLaneNode
            nodeId="signal"
            isActive={isNodeActive('signal')}
            isCompleted={isNodeCompleted('signal')}
          />
        </div>

        {/* Columna 2: Agente */}
        <div className="flex flex-col gap-2">
          <p className="text-[9px] text-slate-400 uppercase tracking-wider font-semibold mb-1 text-center">
            Agente
          </p>
          <SwimLaneNode
            nodeId="agent"
            isActive={isNodeActive('agent')}
            isCompleted={isNodeCompleted('agent')}
          />
        </div>

        {/* Columna 3: Contexto y reglas (Memoria + RAG) */}
        <div className="flex flex-col gap-2">
          <p className="text-[9px] text-slate-400 uppercase tracking-wider font-semibold mb-1 text-center">
            Contexto y reglas
          </p>
          <SwimLaneNode
            nodeId="memory"
            isActive={isNodeActive('memory')}
            isCompleted={isNodeCompleted('memory')}
          />
          <SwimLaneNode
            nodeId="rag"
            isActive={isNodeActive('rag')}
            isCompleted={isNodeCompleted('rag')}
          />
        </div>

        {/* Columna 4: Sistemas y ejecución (Tools + Acción + Resultado) */}
        <div className="flex flex-col gap-2">
          <p className="text-[9px] text-slate-400 uppercase tracking-wider font-semibold mb-1 text-center">
            Sistemas y ejecución
          </p>
          <SwimLaneNode
            nodeId="tools"
            isActive={isNodeActive('tools')}
            isCompleted={isNodeCompleted('tools')}
          />
          <SwimLaneNode
            nodeId="action"
            isActive={isNodeActive('action')}
            isCompleted={isNodeCompleted('action')}
          />
          <SwimLaneNode
            nodeId="result"
            isActive={isNodeActive('result')}
            isCompleted={isNodeCompleted('result')}
          />
        </div>
      </div>

      {/* Nodo de salida manual - solo visible en modo no estructurado paso 7 */}
      {step.toNode === 'human_review' && (
        <div className="mt-4 flex justify-center">
          <SwimLaneNode
            nodeId="human_review"
            isActive={true}
            isCompleted={false}
          />
        </div>
      )}

      {/* Indicador de interacción activa */}
      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
        <span className="font-medium">{NODE_CFG[step.fromNode].label}</span>
        <ArrowRight className="w-3.5 h-3.5 text-violet-500 animate-pulse" />
        <span className="font-medium">{NODE_CFG[step.toNode].label}</span>
      </div>
    </div>
  );
}

// ─── NODO ACTIVO MÓVIL SIMPLIFICADO ─────────────────────────────────
function MobileActiveNode({ step }) {
  const fromCfg = NODE_CFG[step.fromNode];
  const toCfg = NODE_CFG[step.toNode];
  const FromIcon = fromCfg.Icon;
  const ToIcon = toCfg.Icon;

  return (
    <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-3 sm:p-4 backdrop-blur-sm">
      <p className="text-[10px] text-slate-600 uppercase tracking-[0.15em] font-semibold mb-2 sm:mb-3">
        Interacción activa
      </p>
      <div className="flex items-center justify-center gap-4">
        <div className="flex flex-col items-center gap-1.5">
          <span className={`${fromCfg.activeIcon}`}>
            <FromIcon className="w-7 h-7" />
          </span>
          <span className={`text-xs font-semibold ${fromCfg.activeText}`}>
            {fromCfg.label}
          </span>
        </div>
        <ArrowRight className="w-5 h-5 text-violet-400 animate-pulse" />
        <div className="flex flex-col items-center gap-1.5">
          <span className={`${toCfg.activeIcon}`}>
            <ToIcon className="w-7 h-7" />
          </span>
          <span className={`text-xs font-semibold ${toCfg.activeText}`}>
            {toCfg.label}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── CUADRO EXPLICATIVO ──────────────────────────────────────────────
function StepCard({ step, showContent, mode }) {
  const modeData = DEMO_MODES[mode];
  const isLastStep = step.id === 7;

  const statusBadge =
    step.status === 'success'  ? { text: 'INTERVENCIÓN HABILITADA',     cls: 'bg-emerald-950/50 border-emerald-800/60 text-emerald-300' } :
    step.status === 'blocked'  ? { text: 'INTERVENCIÓN NO HABILITADA',  cls: 'bg-rose-950/50 border-rose-800/60 text-rose-300' } :
    step.status === 'warning'  ? { text: 'PROCESO SIN ESTRUCTURA',      cls: 'bg-amber-950/50 border-amber-800/60 text-amber-300' } :
    null;

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 backdrop-blur-sm">
      {/* Encabezado */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">
          Paso {step.id} / 7
        </span>
        {statusBadge && (
          <span className={`text-[9px] uppercase tracking-wide px-2 py-0.5 rounded border ${statusBadge.cls}`}>
            {statusBadge.text}
          </span>
        )}
      </div>

      {/* Área de contenido */}
      <div className="relative min-h-[200px]">
        <div className={`transition-opacity duration-300 ${showContent ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          {/* Título del paso */}
          <h3 className="text-base font-bold text-white mb-2 leading-tight">{step.title}</h3>

          {/* Texto principal */}
          <p className="text-sm text-slate-300 mb-3">{step.mainText}</p>

          {/* Lo siguiente que debe validar el agente (o "que ocurre en el flujo" para salida manual) */}
          <div className="mb-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">
              {/* Labels cortos en mobile */}
              <span className="sm:hidden">
                {step.toNode === 'human_review' ? 'Lo que ocurre' : 'Siguiente validación'}
              </span>
              <span className="hidden sm:inline">
                {step.toNode === 'human_review' ? 'Lo siguiente que ocurre en el flujo' : 'Lo siguiente que debe validar el agente'}
              </span>
            </p>
            <p className="text-xs text-slate-400 leading-relaxed">{step.transitionText}</p>
          </div>

          {/* Registro del sistema */}
          <div className="mb-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">
              <span className="sm:hidden">Registro</span>
              <span className="hidden sm:inline">Registro del sistema</span>
            </p>
            <p className="text-xs font-mono text-slate-600 bg-slate-950/50 px-2 py-1.5 rounded border border-slate-800/50">
              {step.traceText}
            </p>
          </div>

          {/* Bullets */}
          {step.bullets?.length > 0 && (
            <ul className="space-y-1 mb-3">
              {step.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-slate-400">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-600 flex-shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Estado de procesamiento */}
        <div className={`absolute inset-0 flex items-start transition-opacity duration-300 ${!showContent ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <p className="text-sm text-slate-500 animate-pulse pt-1">Procesando...</p>
        </div>
      </div>

      {/* Banner final del ciclo */}
      {isLastStep && showContent && (
        <div className={`rounded-lg px-3 py-2.5 border text-xs leading-relaxed transition-all duration-500 ${
          modeData.bannerVariant === 'success'
            ? 'bg-teal-950/30 border-teal-800/40 text-teal-400/90'
            : 'bg-amber-950/30 border-amber-800/40 text-amber-400/90'
        }`}>
          {modeData.bannerFinal}
        </div>
      )}
    </div>
  );
}

// ─── PASOS NUMERADOS CLICKEABLES ────────────────────────────────────
function ProgressSteps({ current, total, onStepClick }) {
  const stepLabels = ['Señal', 'Memoria', 'Recuperada', 'Reglas', 'Validación', 'Acción', 'Resultado'];
  
  return (
    <div className="flex items-center gap-1.5 sm:gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <button
          key={i}
          onClick={() => onStepClick(i)}
          title={stepLabels[i]}
          className={`
            w-9 h-9 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center text-xs font-semibold
            transition-all duration-300 border cursor-pointer
            ${
              i < current   ? 'bg-violet-950/50 border-violet-800/60 text-violet-400/70' :
              i === current ? 'bg-violet-600 border-violet-500 text-white scale-110 shadow-lg shadow-violet-500/30' :
              'bg-slate-900/50 border-slate-700/50 text-slate-500 hover:bg-slate-800/50 hover:border-slate-600 hover:text-slate-300'
            }
          `}
        >
          {i + 1}
        </button>
      ))}
    </div>
  );
}

// ─── COMPONENTE PRINCIPAL ────────────────────────────────────────────
export default function AgenticFlowDemo({ mode = 'structured' }) {
  const [stepIndex,     setStepIndex]     = useState(0);
  const [isPlaying,     setIsPlaying]     = useState(false); // ← v4.2: false por defecto
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showContent,   setShowContent]   = useState(true);
  const sectionRef = useRef(null);

  const modeData = DEMO_MODES[mode];
  const steps    = modeData.steps;
  const step     = steps[stepIndex];

  // Resetear al cambiar de modo (NO auto-iniciar)
  useEffect(() => {
    setStepIndex(0);
    setShowContent(true);
    setIsPlaying(false); // ← v4.2: no auto-reproducir
    setHasInteracted(false);
  }, [mode]);

  // v4.2: IntersectionObserver solo para scroll tracking (NO auto-iniciar)
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        // Usuario puede ver la demo, pero no forzamos reproducción
        if (entry.isIntersecting && !hasInteracted) {
          // Opcional: marcar como "visible" si necesitas analytics
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasInteracted]);

  // Motor de animación por pasos (nueva secuencia temporal)
  useEffect(() => {
    if (!isPlaying) return;
    if (stepIndex >= steps.length - 1 && showContent) return; // Ya terminó

    const timers = [];

    // t=0: actualizar cuadro explicativo (ocultar contenido primero)
    setShowContent(false);

    // t=250: mostrar contenido del paso
    timers.push(setTimeout(() => setShowContent(true), 250));

    // t=3200: avanzar al siguiente paso (o quedarse en el último)
    if (stepIndex < steps.length - 1) {
      timers.push(setTimeout(() => setStepIndex(i => i + 1), 3200));
    }

    return () => timers.forEach(clearTimeout);
  }, [stepIndex, isPlaying]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── HANDLERS DE NAVEGACIÓN ──────────────────────────────────────
  function handleStepClick(index) {
    setStepIndex(index);
    setShowContent(false);
    setIsPlaying(false); // ← Pausar autoplay al hacer clic
    setHasInteracted(true);
    setTimeout(() => setShowContent(true), 250);
  }

  function handlePrevious() {
    if (stepIndex > 0) {
      setStepIndex(i => i - 1);
      setShowContent(false);
      setIsPlaying(false);
      setHasInteracted(true);
      setTimeout(() => setShowContent(true), 250);
    }
  }

  function handleNext() {
    if (stepIndex < steps.length - 1) {
      setStepIndex(i => i + 1);
      setShowContent(false);
      setIsPlaying(false);
      setHasInteracted(true);
      setTimeout(() => setShowContent(true), 250);
    }
  }

  function handlePlayPause() {
    setIsPlaying(p => !p);
    setHasInteracted(true);
  }

  function handleReset() {
    setStepIndex(0);
    setShowContent(true);
    setIsPlaying(false); // ← v4.2: NO auto-reproducir al reiniciar
    setHasInteracted(true);
  }

  return (
    <section ref={sectionRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

      {/* Desktop: dos columnas (56% / 44%) */}
      <div className="hidden lg:grid lg:grid-cols-[56fr_44fr] gap-6 items-start">
        <DesktopSwimlane stepIndex={stepIndex} steps={steps} />
        <div className="flex flex-col gap-4">
          <StepCard step={step} showContent={showContent} mode={mode} />
          
          {/* Banda de controles unificada v4.2 */}
          <div className="bg-slate-900/40 border border-slate-800/60 rounded-lg px-4 py-3">
            {/* Fila 1: Navegación rápida */}
            <div className="flex items-center justify-between gap-4 mb-3">
              <button
                onClick={handlePrevious}
                disabled={stepIndex === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 hover:text-white border border-slate-700/50"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Anterior
              </button>
              
              <ProgressSteps current={stepIndex} total={steps.length} onStepClick={handleStepClick} />
              
              <button
                onClick={handleNext}
                disabled={stepIndex === steps.length - 1}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 hover:text-white border border-slate-700/50"
              >
                Siguiente
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            
            {/* Fila 2: Reproducción y reinicio */}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={handlePlayPause}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-violet-600/20 hover:bg-violet-600/30 text-violet-300 hover:text-violet-200 border border-violet-500/30"
              >
                {isPlaying ? (
                  <><Pause className="w-3.5 h-3.5" /> Pausar demo</>
                ) : (
                  <><Play className="w-3.5 h-3.5" /> Ver demo guiada</>
                )}
              </button>
              
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 hover:text-slate-200 border border-slate-700/50"
              >
                <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                Reiniciar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: columna única simplificada */}
      <div className="lg:hidden flex flex-col gap-3">
        <MobileActiveNode step={step} />
        <StepCard step={step} showContent={showContent} mode={mode} />
        
        {/* Controles móviles — 3 filas con área táctil mínima 48px */}
        <div className="bg-slate-900/40 border border-slate-800/60 rounded-xl px-4 pt-4 pb-3">
          {/* Fila 1: Steps numerados centrados */}
          <div className="flex items-center justify-center mb-4">
            <ProgressSteps current={stepIndex} total={steps.length} onStepClick={handleStepClick} />
          </div>
          
          {/* Fila 2: Anterior • Reproducir • Siguiente — área táctil completa */}
          <div className="flex items-stretch gap-2 mb-3">
            <button
              onClick={handlePrevious}
              disabled={stepIndex === 0}
              className="flex-1 flex items-center justify-center gap-1 h-12 rounded-xl bg-slate-800/60 text-slate-300 disabled:opacity-30 border border-slate-700/50 text-sm font-medium transition-colors hover:bg-slate-700/60"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Ant.</span>
            </button>
            
            <button
              onClick={handlePlayPause}
              className="flex-[2] flex items-center justify-center gap-2 h-12 rounded-xl bg-violet-600/25 hover:bg-violet-600/35 text-violet-200 border border-violet-500/40 text-sm font-semibold transition-colors"
            >
              {isPlaying
                ? <><Pause className="w-4 h-4" /> Pausar</>
                : <><Play className="w-4 h-4" /> Reproducir</>
              }
            </button>
            
            <button
              onClick={handleNext}
              disabled={stepIndex === steps.length - 1}
              className="flex-1 flex items-center justify-center gap-1 h-12 rounded-xl bg-slate-800/60 text-slate-300 disabled:opacity-30 border border-slate-700/50 text-sm font-medium transition-colors hover:bg-slate-700/60"
            >
              <span>Sig.</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          
          {/* Fila 3: Reiniciar — acción secundaria discreta */}
          <div className="flex justify-center">
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-slate-800/40 hover:bg-slate-700/50 text-slate-500 hover:text-slate-300 border border-slate-700/40 text-xs transition-colors"
            >
              <ArrowRight className="w-3.5 h-3.5 rotate-180" />
              Reiniciar
            </button>
          </div>
        </div>
      </div>

    </section>
  );
}
